import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, MapPin, Users, Calendar, Award } from "lucide-react";

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const milestones = [
  { year: "2011", title: "Founded by Microsoft Alumni", desc: "Ranjana Jain, ex-Microsoft trainer and evangelist, launches Technofocus with a vision for high-quality, scalable cloud training." },
  { year: "2015", title: "Microsoft Learning Partner", desc: "Officially recognized as a Microsoft Learning Partner, enabling delivery of official Microsoft training content globally." },
  { year: "2018", title: "LevelUp Platform Launch", desc: "Launched LevelUp, our self-paced learning platform built for Microsoft Partners worldwide." },
  { year: "2021", title: "EventsUp & StepUp", desc: "Expanded the ecosystem with EventsUp (live events) and StepUp (simulated labs), completing a full training lifecycle." },
  { year: "2024", title: "10M+ Professionals Trained", desc: "Crossed 10 million individuals trained globally — a testament to our commitment to quality and scale." },
];

const offices = [
  { city: "Singapore (HQ)", address: "138 Robinson Road, #26-01 Oxley Tower, Singapore 068906" },
  { city: "New Delhi, India", address: "K-1, Green Park, New Delhi-110016" },
];

const teamStats = [
  { icon: Users, value: "100+", label: "Training Experts" },
  { icon: Calendar, value: "14+", label: "Years in Business" },
  { icon: Award, value: "800+", label: "Courses Delivered" },
  { icon: MapPin, value: "2", label: "Global Offices" },
];

export default function About() {
  const missionSection = useReveal();
  const timelineSection = useReveal();
  const teamSection = useReveal();
  const officeSection = useReveal();

  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: '#FFF8F3' }}>
      {/* ── HERO ── */}
      <section data-testid="about-hero-section" className="pt-32 pb-20"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5" style={{ color: '#D4EC8E' }}>About Technofocus</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Built by Microsoft Alumni,<br />
            <span style={{ color: '#D4EC8E', fontWeight: 600 }}>Trusted by the<br />Microsoft Community</span>
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Since 2011, we've been at the forefront of Microsoft cloud training — transforming how professionals and organizations embrace the full potential of Microsoft technologies.
          </p>
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section ref={missionSection.ref} data-testid="mission-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div
              data-testid="mission-card"
              className={`p-10 rounded-3xl border bg-white shadow-sm hover:shadow-lg transition-all duration-500 ${missionSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ borderColor: '#E2EBE9' }}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: '#E5F3F0' }}>
                <Target size={22} style={{ color: '#194449' }} />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                To transform the cloud computing learning experience by providing hands-on, value-driven training that equips professionals with the skills needed to excel in today's digital landscape. We empower individuals to enhance their expertise, drive organizational success, and stay ahead of technological advancements through flexible, expert-led programs.
              </p>
            </div>
            <div
              data-testid="vision-card"
              className={`p-10 rounded-3xl text-white shadow-sm hover:shadow-lg transition-all duration-500 delay-100 ${missionSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ background: 'linear-gradient(135deg, #194449, #091F2C)' }}
            >
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Eye size={22} className="text-white" />
              </div>
              <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Vision</h2>
              <p className="leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                To establish Technofocus as a dynamic and innovative learning hub where motivated and skilled professionals drive the seamless transformation of learners into industry-leading experts. We aim to create a vibrant, value-driven ecosystem that empowers individuals to reach their full potential in cloud computing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STORY ── */}
      <section data-testid="story-section" className="py-20" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: '#194449' }}>Our Story</p>
              <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>A Vision Born from Microsoft</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Technofocus was founded in 2011 by Ranjana Jain, following her successful tenure at Microsoft, where she gained invaluable experience as a trainer, trainer-of-trainers, technology consultant, and evangelist. Her passion for high-quality, technical education drove her to create Technofocus.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                Her vision was clear: to deliver in-depth, scalable learning experiences that empower both students and industry professionals with the expertise needed to excel in the fast-evolving world of technology.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Today, Technofocus is a globally recognized Microsoft Learning Partner having trained over 10 million professionals from organizations including EY, KPMG, Microsoft, Amdocs, and many more.
              </p>
              <Link to="/contact" data-testid="about-contact-button"
                className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold transition-colors"
                style={{ backgroundColor: '#194449' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#0B2E38'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#194449'}>
                Get in Touch <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <img src="/team-photo.jpeg"
                alt="Technofocus team at Microsoft AI Partner Training Roadshow" className="rounded-2xl w-full object-cover shadow-xl" style={{ height: 440 }} />
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM STATS ── */}
      <section ref={teamSection.ref} data-testid="team-stats-section"
        style={{ background: 'linear-gradient(135deg, #091F2C, #194449)' }}
        className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {teamStats.map((stat, i) => (
              <div key={stat.label} data-testid={`team-stat-${i}`}
                className={`text-center transition-all duration-700 ${teamSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon size={22} style={{ color: '#D4EC8E' }} />
                </div>
                <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <div className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section ref={timelineSection.ref} data-testid="timeline-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className={`text-center mb-14 transition-all duration-700 ${timelineSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Our Journey</p>
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Key Milestones</h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px" style={{ backgroundColor: '#E2EBE9' }}></div>
            {milestones.map((m, i) => (
              <div key={m.year} data-testid={`milestone-${i}`}
                className={`relative flex gap-6 mb-10 transition-all duration-700 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} ${timelineSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}>
                <div className={`md:w-1/2 ${i % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'} pl-14 md:pl-0`}>
                  <div className="inline-block text-sm font-bold px-3 py-1 rounded-full mb-2" style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}>{m.year}</div>
                  <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{m.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                </div>
                <div className="absolute left-4 md:left-1/2 top-1 w-4 h-4 rounded-full border-4 border-white shadow-sm md:-translate-x-1/2" style={{ backgroundColor: '#194449' }}></div>
                <div className="md:w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OFFICES ── */}
      <section ref={officeSection.ref} data-testid="offices-section" className="py-20" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className={`text-center mb-12 transition-all duration-700 ${officeSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#194449' }}>Global Presence</p>
            <h2 className="text-3xl md:text-4xl font-medium text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Offices</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offices.map((office, i) => (
              <div key={office.city} data-testid={`office-card-${i}`}
                className={`bg-white rounded-2xl p-7 border shadow-sm hover:shadow-lg transition-all duration-300 ${officeSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms`, borderColor: '#E2EBE9' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#E5F3F0' }}>
                  <MapPin size={18} style={{ color: '#194449' }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{office.city}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{office.address}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section data-testid="about-cta-section" style={{ background: 'linear-gradient(135deg, #091F2C, #194449)' }} className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Partner with Technofocus?</h2>
          <p className="mb-7" style={{ color: 'rgba(255,255,255,0.65)' }}>Explore our training programs or get in touch to discuss how we can support your Microsoft learning journey.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services" data-testid="about-services-button"
              className="px-7 py-3.5 rounded-full font-semibold transition-colors"
              style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}>
              Explore Services
            </Link>
            <Link to="/contact" data-testid="about-contact-btn"
              className="border text-white hover:bg-white/10 px-7 py-3.5 rounded-full font-semibold transition-colors"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
